import { useAuthenticator } from '@aws-amplify/ui-react';
import { signOut } from 'aws-amplify/auth';
import { I18n } from 'aws-amplify/utils';
import type React from 'react';
import { useEffect } from 'react';
import type { RoutesProps } from 'react-router-dom';
import { Route, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import Layout from './components/layouts/layout/layout';
import DriveList from './components/pages/drive/list/driveList';
import LogoutPage from './components/pages/logout';
import { Offering } from './components/pages/offering';
import { Login } from './pages/login';
import { settingState } from './state/settingState';
import { DriveDetails } from '@/components/pages/drive/details/driveDetails';
import { DriveRoutes } from '@/components/pages/driveRoutes/driveRoutes';
import { NotFoundPage } from '@/components/pages/error/NotFoundPage';
import { MenuPage } from '@/components/pages/menu';
import { NotificationPage } from '@/components/pages/notification';
import { OperationFinishConfirmPage } from '@/components/pages/operationFinishConfirm';
import { SelectPage } from '@/components/pages/vehicle-driver/select';
import { UpdatePage } from '@/components/pages/vehicle-driver/update';
import { WaypointConfirmPage } from '@/components/pages/waypoint';
import myDict from '@/i18n/amplify18n';
import { usePageTracking } from '@/modules/hooks/usePageTracking';
import { useServiceType } from '@/modules/hooks/useServiceType';
import { initAmplify } from '@/modules/utils/init-amplify';
import * as paths from '@/routes/paths';
import { ServiceType } from '@/types/serviceType';
import '@/i18n/configs';

export type AppProps = {
  RoutesComponent: React.ComponentType<RoutesProps>;
};

initAmplify();
I18n.putVocabularies(myDict);
I18n.setLanguage('ja');

const TempLogout: React.FC = () => {
  const navigate = useNavigate();
  const [, setSetting] = useRecoilState(settingState);
  useEffect(() => {
    signOut()
      .then(() => {
        setSetting(undefined);
        navigate(paths.login);
      })
      .catch(() => {});
  }, [navigate, setSetting]);

  return '';
};

const TempPaga: React.FC = () => {
  const navigate = useNavigate();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const serviceType = useServiceType();
  const [, setSetting] = useRecoilState(settingState);

  useEffect(() => {
    if (['authenticated', 'configuring'].includes(authStatus) && serviceType) {
      if (serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) {
        navigate(paths.vehicleDriverSelect);
      }

      if (serviceType === ServiceType.PreBooking) {
        // ドライブ一覧画面に遷移する
        navigate(paths.driveList);
      }
    } else {
      signOut()
        .then(() => {
          setSetting(undefined);
          navigate(paths.login);
        })
        .catch(() => {});
    }
  }, [authStatus, serviceType, navigate, setSetting]);

  return '';
};

const App: React.FC<AppProps> = ({ RoutesComponent }) => {
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);

  const serviceType = useServiceType();
  const getElement = (element: JSX.Element) => {
    if (['authenticated', 'configuring'].includes(authStatus) && serviceType) {
      return element;
    }

    return <TempLogout />;
  };

  const location = useLocation();
  const navigate = useNavigate();
  usePageTracking();

  useEffect(() => {
    if (['authenticated', 'configuring'].includes(authStatus)) {
      if ([paths.login].includes(location.pathname)) {
        // 定時定路線、リアルタイムオンデマンドの場合
        if (serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) {
          navigate(paths.vehicleDriverSelect);
        }

        // 事前予約の場合
        if (serviceType === ServiceType.PreBooking) {
          // ドライブ一覧画面に遷移する
          navigate(paths.driveList);
        }
      }
    }
  }, [authStatus, serviceType, location.pathname, navigate]);

  return (
    <Layout>
      <RoutesComponent>
        {['unauthenticated', 'configuring'].includes(authStatus) && (
          <>
            <Route path={paths.top} element={<TempPaga />} />
            <Route path={paths.login} element={<Login />} />
            <Route path="*" element={<NotFoundPage />} />
          </>
        )}
        {['authenticated', 'configuring'].includes(authStatus) && (
          <>
            <Route path={paths.top} element={<TempPaga />} />
            <Route path={paths.login} element={<Login />} />
            <Route path={paths.logout} element={getElement(<LogoutPage />)} />
            <Route path={paths.reservation} element={<NotFoundPage />} />
            <Route path={paths.driveList} element={getElement(<DriveList />)} />
            <Route path={paths.driveDetails} element={getElement(<DriveDetails />)} />
            <Route path={paths.menu} element={getElement(<MenuPage />)} />
            <Route path={paths.notification} element={getElement(<NotificationPage />)} />
            <Route path={paths.vehicleDriverUpdate} element={getElement(<UpdatePage />)} />
            <Route path={paths.vehicleDriverSelect} element={getElement(<SelectPage />)} />
            <Route path={paths.operationFinishConfirm} element={getElement(<OperationFinishConfirmPage />)} />
            <Route path={paths.offering} element={getElement(<Offering />)} />
            <Route path={paths.waypointConfirm} element={getElement(<WaypointConfirmPage />)} />

            <Route path={paths.driveRoutes} element={getElement(<DriveRoutes />)} />

            <Route path="*" element={<NotFoundPage />} />
          </>
        )}
      </RoutesComponent>
    </Layout>
  );
};

export default App;
